import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bookmark, Filter, MoreHorizontal, Pencil, Play, Trash2 } from 'lucide-react';
import { Badge } from '$lib/components/ui/Badge';
import { Button } from '$lib/components/ui/Button';
import { Chip } from '$lib/components/ui/Chip';
import { Input } from '$lib/components/ui/Input';
import { StatusPill, type RunStatus } from '$lib/components/ui/StatusPill';
import {
  StatusDot,
  severityCardBorder,
  severityFromStatus,
  severityHeaderStrip,
  type Severity
} from '$lib/components/ui/StatusDot';
import { StageProgress, type Stage } from '$lib/components/ui/StageProgress';
import { CancelRunDialog } from '$lib/components/ui/ConfirmDialog';
import { Skeleton, SkeletonCard, SkeletonTableRow } from '$lib/components/ui/Skeleton';
import { Sparkline, type SparkPoint } from '$lib/components/charts/Sparkline';
import { SavedSearchSummaryLine } from '$lib/components/savedSearches/SavedSearchSummaryLine';
import { BbrAttributesPanel } from '$lib/components/detail/BbrAttributesPanel';
import { EventLogTimeline } from '$lib/components/detail/EventLogTimeline';
import { AggregateStats } from '$lib/components/comparables/AggregateStats';
import { ScoreHistogram } from '$lib/components/comparables/ScoreHistogram';
import { ComparableRow } from '$lib/components/comparables/ComparableRow';
import { UndoStrip } from '$lib/components/comparables/UndoStrip';
import type {
  BbrAttributes,
  ComparableDTO,
  ComparablesAggregate,
  ComparablesTarget,
  UnitEvent
} from '$lib/api/types';
import {
  formatDate,
  formatKr,
  formatKrPerSqm,
  formatRooms,
  formatSqm,
  formatYear
} from '$lib/lib/formatters';
import { cn } from '$lib/lib/cn';

// /dev/components — living component catalog (decision 5).
// Each section pins a token, primitive, or pattern. Snapshot tests run
// against this page (DEV-106 scope).

interface SectionProps {
  id: string;
  title: string;
  description?: string;
  children: React.ReactNode;
}

function Section({ id, title, description, children }: SectionProps) {
  return (
    <section id={id} className="border-t border-rule py-6">
      <header className="mb-3">
        <h2 className="text-sm font-semibold text-fg">{title}</h2>
        {description && <p className="mt-1 text-xs text-fg-muted">{description}</p>}
      </header>
      <div className="rounded border border-rule bg-surface p-4">{children}</div>
    </section>
  );
}

function ColorSwatch({ label, varName }: { label: string; varName: string }) {
  return (
    <div className="flex items-center gap-2">
      <span
        aria-hidden
        className="h-8 w-8 shrink-0 rounded border border-rule"
        style={{ background: `rgb(var(${varName}))` }}
      />
      <div className="text-xs">
        <div className="font-medium text-fg">{label}</div>
        <code className="text-2xs text-fg-muted">{varName}</code>
      </div>
    </div>
  );
}

const sparkSampleUp: SparkPoint[] = Array.from({ length: 12 }, (_, i) => ({
  ts: new Date(2025, 4 + i, 1).toISOString(),
  priceKrPerMonth: 14000 + i * 220 + (i % 3) * 80
}));

const sparkSampleDown: SparkPoint[] = Array.from({ length: 12 }, (_, i) => ({
  ts: new Date(2025, 4 + i, 1).toISOString(),
  priceKrPerMonth: 18500 - i * 140 + (i % 4) * 60
}));

const adminRunStatuses: RunStatus[] = [
  'pending',
  'running',
  'paused',
  'done',
  'done-with-errors',
  'failed',
  'cancelled'
];

const adminPortalSamples: Array<{ name: string; status: RunStatus; lastRun: string; listings: number }> = [
  { name: 'BoligPortal', status: 'done', lastRun: '02:14 today', listings: 1240 },
  { name: 'Lejebolig', status: 'running', lastRun: 'now', listings: 412 },
  { name: 'Lejeboligportal', status: 'done-with-errors', lastRun: '02:18 today', listings: 980 },
  { name: 'BoligZonen', status: 'failed', lastRun: 'yesterday', listings: 0 }
];

export function DevComponentsPage() {
  const { t } = useTranslation();
  const [cancelRunOpen, setCancelRunOpen] = useState(false);
  const sampleBbr: BbrAttributes = useMemo(
    () => ({
      bbrId: 'BBR-00042',
      buildingId: 'BLD-vester-1620',
      buildingType: 'apartment',
      yearBuilt: 1932,
      yearRenovated: 2008,
      m2: 78.4,
      rooms: 3,
      floor: 2,
      side: 'tv',
      hasBalcony: true,
      hasElevator: false,
      energyRating: 'C',
      heating: 'district',
      resolutionConfidence: 0.94
    }),
    []
  );

  const sampleEvents: UnitEvent[] = useMemo(() => {
    const now = Date.now();
    return [
      { id: '1', ts: new Date(now - 1000 * 60 * 60 * 24 * 1).toISOString(), kind: 'price_change', delta: { from: 19200, to: 18500, field: 'rentKrPerMonth' }, source: 'BoligPortal' },
      { id: '2', ts: new Date(now - 1000 * 60 * 60 * 24 * 9).toISOString(), kind: 'bbr_change', delta: { field: 'energyRating', from: 'D', to: 'C' }, note: 'Updated from weekly BBR refresh' },
      { id: '3', ts: new Date(now - 1000 * 60 * 60 * 24 * 32).toISOString(), kind: 'listed', source: 'BoligPortal', note: 'Listed for rent' }
    ];
  }, []);

  const adminStageOrder = ['scrape', 'normalize', 'diff', 'persist', 'enrich'] as const;
  const stageLabel = (id: string) => t(`admin.stage.${id}`);

  const stagesAllDone: Stage[] = adminStageOrder.map((id) => ({
    id,
    label: stageLabel(id),
    status: 'done'
  }));
  stagesAllDone[0].count = '12 pages';
  stagesAllDone[1].count = '1,204';
  stagesAllDone[2].count = '+38 / −12';
  stagesAllDone[3].count = '1,204';
  stagesAllDone[4].count = '982 / 1,204';

  const stagesMidRun: Stage[] = adminStageOrder.map((id, i) => ({
    id,
    label: stageLabel(id),
    status: (['done', 'done', 'running', 'pending', 'pending'] as Stage['status'][])[i]
  }));
  stagesMidRun[0].count = '12 pages';
  stagesMidRun[1].count = '1,204';
  stagesMidRun[2].count = '…';

  const stagesFailed: Stage[] = adminStageOrder.map((id, i) => ({
    id,
    label: stageLabel(id),
    status: (['done', 'done-with-errors', 'failed', 'pending', 'pending'] as Stage['status'][])[i]
  }));
  stagesFailed[0].count = '12 pages';
  stagesFailed[1].count = '1,180 (24 errors)';

  const tableSample = useMemo(
    () => [
      {
        addr: 'Vesterbrogade 25, 2. tv',
        city: 'København V',
        m2: 78.4,
        rooms: 3,
        year: 1932,
        kr: 18500,
        krPerSqm: 236,
        spark: sparkSampleUp,
        status: 'active' as const
      },
      {
        addr: 'Sønder Boulevard 12, 4.',
        city: 'København V',
        m2: 64.0,
        rooms: 2,
        year: 1908,
        kr: 14250,
        krPerSqm: 222,
        spark: sparkSampleDown,
        status: 'pending' as const
      },
      {
        addr: 'Trøjborgvej 60, 1. th',
        city: 'Aarhus N',
        m2: 92.5,
        rooms: 4,
        year: 1956,
        kr: 12900,
        krPerSqm: 139,
        spark: sparkSampleUp.slice(2),
        status: 'active' as const
      }
    ],
    []
  );

  return (
    <div className="flex flex-1 overflow-y-auto">
      <aside className="sticky top-0 hidden h-fit w-44 shrink-0 self-start border-r border-rule bg-surface-2 p-4 lg:block">
        <h2 className="text-2xs uppercase tracking-wider text-fg-subtle">Catalog</h2>
        <ul className="mt-2 flex flex-col gap-1 text-xs">
          {[
            ['#colors', 'Colors'],
            ['#typography', 'Typography'],
            ['#spacing', 'Spacing'],
            ['#shadows', 'Shadows & radius'],
            ['#buttons', 'Buttons'],
            ['#inputs', 'Inputs'],
            ['#badges', 'Badges'],
            ['#chips', 'Filter chips'],
            ['#sparkline', 'Sparkline'],
            ['#table-row', 'Table row'],
            ['#status-pills', 'Status pills (admin)'],
            ['#severity', 'Severity (admin)'],
            ['#stage-progress', 'Stage progress (admin)'],
            ['#confirm-dialog', 'Confirmation modal'],
            ['#skeletons', 'Skeleton states'],
            ['#status', 'Status patterns'],
            ['#saved-search', 'Saved searches'],
            ['#bbr-panel', 'BBR attributes'],
            ['#event-log', 'Event log'],
            ['#comparables', 'Comparables (M4)']
          ].map(([href, label]) => (
            <li key={href}>
              <a href={href} className="text-fg-muted hover:text-accent">
                {label}
              </a>
            </li>
          ))}
        </ul>
      </aside>
      <div className="min-w-0 flex-1 px-6 pb-12">
        <header className="pt-6">
          <h1 className="text-xl font-semibold text-fg">Components catalog</h1>
          <p className="mt-1 text-xs text-fg-muted">
            Living reference for RentalLens design tokens and primitives. Light + dark
            variants are toggled via the top-bar theme button — both are snapshot-tested
            (DEV-106).
          </p>
        </header>

        <Section id="colors" title="Colors" description="All values are CSS variables on :root. Use semantic tokens — never raw Tailwind palette colors.">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            <ColorSwatch label="bg" varName="--color-bg" />
            <ColorSwatch label="surface" varName="--color-surface" />
            <ColorSwatch label="surface-2" varName="--color-surface-2" />
            <ColorSwatch label="surface-3" varName="--color-surface-3" />
            <ColorSwatch label="fg" varName="--color-fg" />
            <ColorSwatch label="fg-muted" varName="--color-fg-muted" />
            <ColorSwatch label="fg-subtle" varName="--color-fg-subtle" />
            <ColorSwatch label="rule" varName="--color-rule" />
            <ColorSwatch label="rule-strong" varName="--color-rule-strong" />
            <ColorSwatch label="accent" varName="--color-accent" />
            <ColorSwatch label="positive" varName="--color-positive" />
            <ColorSwatch label="warning" varName="--color-warning" />
            <ColorSwatch label="danger" varName="--color-danger" />
            <ColorSwatch label="info" varName="--color-info" />
          </div>
        </Section>

        <Section id="typography" title="Typography" description="System sans for chrome, monospace for numerics. Body 13px / 18px line-height (locked density).">
          <div className="grid gap-3">
            <div>
              <div className="text-2xl text-fg">Heading 2xl · 24/30</div>
              <div className="text-xl text-fg">Heading xl · 20/26</div>
              <div className="text-lg text-fg">Heading lg · 16/22</div>
              <div className="text-md text-fg">Body md · 14/20</div>
              <div className="text-sm text-fg">Body sm · 13/18 (default)</div>
              <div className="text-xs text-fg-muted">Caption xs · 12/16</div>
              <div className="text-2xs text-fg-subtle">Caption 2xs · 11/14</div>
            </div>
            <div className="rounded-sm border border-rule bg-surface-2 p-3">
              <div className="text-2xs uppercase tracking-wider text-fg-subtle">Tabular numerics</div>
              <div className="num mt-1 text-md text-fg">{formatKrPerSqm(218)}</div>
              <div className="num text-sm text-fg-muted">{formatSqm(78.4)} · {formatRooms(3)} rooms · {formatYear(1932)}</div>
            </div>
          </div>
        </Section>

        <Section id="spacing" title="Spacing" description="4px base. Row height locked at 32px (decision 1) and exposed as `h-row`.">
          <div className="flex items-end gap-3">
            {[2, 4, 8, 12, 16, 24, 32].map((px) => (
              <div key={px} className="flex flex-col items-center text-2xs text-fg-muted">
                <span style={{ height: px }} className="block w-8 rounded-xs bg-accent/40" />
                <span className="num mt-1">{px}px</span>
              </div>
            ))}
          </div>
        </Section>

        <Section id="shadows" title="Shadows & radius" description="Restrained — no drop-shadows on tables.">
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded border border-rule bg-surface p-4 shadow-elev-1">
              <div className="text-xs font-medium text-fg">elev-1</div>
              <div className="text-2xs text-fg-muted">subtle hairline</div>
            </div>
            <div className="rounded border border-rule bg-surface p-4 shadow-elev-2">
              <div className="text-xs font-medium text-fg">elev-2</div>
              <div className="text-2xs text-fg-muted">popovers, tooltips</div>
            </div>
            <div className="rounded border border-rule bg-surface p-4 shadow-elev-3">
              <div className="text-xs font-medium text-fg">elev-3</div>
              <div className="text-2xs text-fg-muted">menus, dialogs</div>
            </div>
          </div>
        </Section>

        <Section id="buttons" title="Buttons">
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="primary">Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="danger">Danger</Button>
            <Button variant="primary" disabled>Disabled</Button>
            <Button size="sm" variant="secondary">Small</Button>
          </div>
        </Section>

        <Section id="inputs" title="Inputs">
          <div className="grid max-w-md gap-2">
            <Input placeholder="Search address" />
            <Input defaultValue="Vesterbrogade 25" />
            <Input disabled placeholder="Disabled" />
          </div>
        </Section>

        <Section id="badges" title="Badges">
          <div className="flex flex-wrap items-center gap-2">
            <Badge>Default</Badge>
            <Badge tone="positive">Active</Badge>
            <Badge tone="warning">Provisional</Badge>
            <Badge tone="danger">Delisted</Badge>
            <Badge tone="info">BBR</Badge>
            <Badge tone="accent">Saved</Badge>
          </div>
        </Section>

        <Section
          id="chips"
          title="Filter chips"
          description="6 inline + 'More filters' overflow (decision 6). Active chip shows count and a clear affordance."
        >
          <div className="flex flex-wrap items-center gap-2">
            <Chip>Postnummer</Chip>
            <Chip active count={2} onClear={() => undefined}>
              m²
            </Chip>
            <Chip>Rooms</Chip>
            <Chip active count={1} onClear={() => undefined}>
              Year
            </Chip>
            <Chip>Price</Chip>
            <Chip>Status</Chip>
            <Chip icon={<Filter size={12} />}>More filters</Chip>
          </div>
        </Section>

        <Section
          id="sparkline"
          title="Sparkline"
          description="80×16 inline. Hover surfaces latest observation date + price (decision 12). Stroke color tracks trend direction."
        >
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <Sparkline data={sparkSampleUp} />
              <span className="text-xs text-fg-muted">12-month upward trend</span>
            </div>
            <div className="flex items-center gap-3">
              <Sparkline data={sparkSampleDown} />
              <span className="text-xs text-fg-muted">12-month downward trend</span>
            </div>
            <div className="flex items-center gap-3">
              <Sparkline data={[]} />
              <span className="text-xs text-fg-muted">Empty state</span>
            </div>
          </div>
        </Section>

        <Section
          id="table-row"
          title="Table row"
          description="32px row height (decision 1), 13px body, monospace right-aligned numerics, sparkline on the right. Real virtualization lands in slice S5."
        >
          <div className="overflow-hidden rounded-sm border border-rule">
            <table className="w-full table-fixed border-collapse text-sm">
              <thead className="bg-surface-2 text-2xs uppercase tracking-wider text-fg-subtle">
                <tr>
                  <th className="w-[32%] px-3 py-2 text-left font-medium">Address</th>
                  <th className="w-[14%] px-3 py-2 text-left font-medium">City</th>
                  <th className="w-[8%] px-3 py-2 text-right font-medium">m²</th>
                  <th className="w-[8%] px-3 py-2 text-right font-medium">Rooms</th>
                  <th className="w-[8%] px-3 py-2 text-right font-medium">Year</th>
                  <th className="w-[10%] px-3 py-2 text-right font-medium">Rent</th>
                  <th className="w-[10%] px-3 py-2 text-right font-medium">kr/m²</th>
                  <th className="w-[8%] px-3 py-2 text-right font-medium">12mo</th>
                  <th className="w-[2%] px-3 py-2 text-left font-medium" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {tableSample.map((row, i) => (
                  <tr
                    key={i}
                    className={cn(
                      'h-row text-fg row-line last:border-b-0 hover:bg-surface-2',
                      i % 2 === 1 && 'bg-surface-2/40'
                    )}
                  >
                    <td className="truncate px-3">{row.addr}</td>
                    <td className="truncate px-3 text-fg-muted">{row.city}</td>
                    <td className="num px-3">{formatSqm(row.m2)}</td>
                    <td className="num px-3">{formatRooms(row.rooms)}</td>
                    <td className="num px-3">{formatYear(row.year)}</td>
                    <td className="num px-3">{formatKr(row.kr)}</td>
                    <td className="num px-3">{formatKrPerSqm(row.krPerSqm)}</td>
                    <td className="px-3 text-right">
                      <Sparkline data={row.spark} />
                    </td>
                    <td className="px-2">
                      <button
                        type="button"
                        aria-label="Row actions"
                        className="grid h-6 w-6 place-items-center rounded-xs text-fg-subtle hover:bg-surface-3 hover:text-fg"
                      >
                        <MoreHorizontal size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section
          id="saved-search"
          title="Saved searches"
          description="Local save/restore (decision 8). Each entry captures the URL query string at save time. Alerts deferred to Phase 1.5 (decision 15)."
        >
          <ul className="overflow-hidden rounded-sm border border-rule">
            {[
              {
                name: 'Vesterbro · 60-80 m² · 2 rooms+',
                summary: {
                  q: undefined,
                  postnummerCount: 2,
                  m2Range: [60, 80] as [number | undefined, number | undefined],
                  roomsRange: [2, undefined] as [number | undefined, number | undefined],
                  statusCount: 1,
                  filterCount: 4,
                  sort: 'krPerSqm',
                  dir: 'desc'
                },
                createdAt: '2026-04-29T09:12:00Z'
              },
              {
                name: 'Aarhus C · pre-1950',
                summary: {
                  postnummerCount: 1,
                  yearRange: [undefined, 1950] as [number | undefined, number | undefined],
                  filterCount: 2,
                  sort: 'yearBuilt',
                  dir: 'asc'
                },
                createdAt: '2026-04-22T11:30:00Z'
              }
            ].map((item, i) => (
              <li key={i} className="row-line flex items-start gap-3 px-3 py-2 last:border-b-0 hover:bg-surface-2">
                <Bookmark size={14} className="mt-0.5 shrink-0 text-fg-subtle" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-fg">{item.name}</div>
                  <div className="mt-0.5 truncate text-xs">
                    <SavedSearchSummaryLine summary={item.summary} />
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button size="sm" variant="secondary"><Play size={12} />Restore</Button>
                  <Button size="sm" variant="ghost" aria-label="Rename"><Pencil size={12} /></Button>
                  <Button size="sm" variant="ghost" aria-label="Delete"><Trash2 size={12} /></Button>
                </div>
              </li>
            ))}
          </ul>
        </Section>

        <Section
          id="bbr-panel"
          title="BBR attributes panel"
          description="Lives at the top of the unit detail page. Densely-packed key facts pulled from the BBR/DAR pipeline."
        >
          <BbrAttributesPanel bbr={sampleBbr} />
        </Section>

        <Section
          id="event-log"
          title="Event log timeline"
          description="Chronological feed of changes for a single unit. Reflects the same change-event stream the Signals destination uses (decision 8 of DEV-94)."
        >
          <div className="max-w-md">
            <EventLogTimeline events={sampleEvents} />
          </div>
        </Section>

        <Section
          id="status-pills"
          title="Status pills (admin)"
          description="Run/portal status taxonomy used by the admin dashboard. Tone + icon track admin.status.* (DEV-217). Running pulls a spinner; done-with-errors carries an optional error count."
        >
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {adminRunStatuses.map((s) => (
                <StatusPill key={s} status={s} />
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill status="done-with-errors" errorCount={3} />
              <StatusPill status="running" label="Sweeping pages…" />
            </div>
          </div>
        </Section>

        <Section
          id="severity"
          title="Severity scale (admin)"
          description="Graduated severity used by status dots, portal card borders, and the dashboard header. Maps to existing positive / warning / danger tokens — no new colour vars."
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-4">
              {(['healthy', 'degraded', 'failed', 'unknown'] as Severity[]).map((s) => (
                <div key={s} className="flex items-center gap-2 text-xs text-fg">
                  <StatusDot severity={s} size="md" pulse={s === 'degraded'} />
                  <span>{s}</span>
                </div>
              ))}
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {adminPortalSamples.map((p) => {
                const sev = severityFromStatus(p.status);
                return (
                  <div
                    key={p.name}
                    className={cn(
                      'rounded p-3 shadow-elev-1 bg-surface',
                      severityCardBorder(sev)
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <StatusDot severity={sev} pulse={p.status === 'running'} />
                      <span className="text-sm font-medium text-fg">{p.name}</span>
                      <StatusPill status={p.status} className="ml-auto" />
                    </div>
                    <div className="mt-2 text-2xs text-fg-muted">
                      {t('admin.portal.lastRun', { time: p.lastRun })} ·{' '}
                      <span className="num">{p.listings}</span> listings
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={cn('rounded px-3 py-2 text-xs text-fg', severityHeaderStrip('degraded'))}>
              Dashboard header strip · degraded · {t('admin.dashboard.summary', {
                healthy: 2,
                total: 4,
                degraded: 1,
                failed: 1
              })}
            </div>
          </div>
        </Section>

        <Section
          id="stage-progress"
          title="Stage progress (admin)"
          description="Horizontal 5-step pipeline used on the run-detail page. Stacks vertically below sm. Connector colour leaves the source step so done → running reads as a green tail into a blue ring."
        >
          <div className="flex flex-col gap-6">
            <StageProgress stages={stagesAllDone} />
            <StageProgress stages={stagesMidRun} />
            <StageProgress stages={stagesFailed} />
          </div>
        </Section>

        <Section
          id="confirm-dialog"
          title="Confirmation modal"
          description="Reusable destructive-action confirmation. Cancel-run preset wraps it with the admin.confirm.cancelRun.* copy."
        >
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="danger" size="sm" onClick={() => setCancelRunOpen(true)}>
              {t('admin.action.cancel')}
            </Button>
            <p className="text-2xs text-fg-muted">
              Esc / backdrop click / Keep running all dismiss without firing the action.
            </p>
          </div>
          <CancelRunDialog
            open={cancelRunOpen}
            onConfirm={() => setCancelRunOpen(false)}
            onDismiss={() => setCancelRunOpen(false)}
          />
        </Section>

        <Section
          id="skeletons"
          title="Skeleton states"
          description="Pulse honours prefers-reduced-motion. Card layout matches the live PortalCard so hand-off swap doesn't reflow; row widths echo the recent-runs columns."
        >
          <div className="flex flex-col gap-4">
            <div className="grid gap-3 md:grid-cols-2">
              <SkeletonCard />
              <SkeletonCard />
            </div>
            <div className="overflow-hidden rounded-sm border border-rule">
              <table className="w-full table-fixed border-collapse text-sm">
                <thead className="bg-surface-2 text-2xs uppercase tracking-wider text-fg-subtle">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">{t('admin.recentRuns.columns.portal')}</th>
                    <th className="px-3 py-2 text-left font-medium">{t('admin.recentRuns.columns.started')}</th>
                    <th className="px-3 py-2 text-left font-medium">{t('admin.recentRuns.columns.duration')}</th>
                    <th className="px-3 py-2 text-left font-medium">{t('admin.recentRuns.columns.status')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('admin.recentRuns.columns.found')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('admin.recentRuns.columns.saved')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('admin.recentRuns.columns.errors')}</th>
                  </tr>
                </thead>
                <tbody>
                  <SkeletonTableRow />
                  <SkeletonTableRow />
                  <SkeletonTableRow />
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Skeleton width={140} height={14} />
              <Skeleton width={80} height={14} muted />
              <Skeleton width="100%" height={11} className="max-w-md" muted />
            </div>
          </div>
        </Section>

        <Section id="status" title="Status patterns" description="Provisional badge for out-of-scope addresses (decision 11). Stale-data treatment for comparables recompute (decision 10).">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-sm border border-rule bg-surface p-3">
              <div className="text-xs font-medium text-fg">Out-of-scope address</div>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-sm text-fg">Strandvejen 12, Faaborg</span>
                <Badge tone="warning">Provisional · live BBR</Badge>
              </div>
              <p className="mt-2 text-2xs text-fg-muted">
                Outside Phase 1 scope (Copenhagen, Aarhus, Odense, Aalborg). Resolved
                via live DAR/BBR lookup; data quality not equivalent to in-scope cities.
              </p>
            </div>
            <div className="rounded-sm border border-rule bg-surface p-3">
              <div className="text-xs font-medium text-fg">Stale aggregate (recompute)</div>
              <div className="mt-2 h-1 w-full overflow-hidden rounded-xs bg-surface-3">
                <div className="h-full w-1/2 animate-pulse bg-accent" />
              </div>
              <div className="mt-2 grid grid-cols-3 gap-3 opacity-60">
                <div>
                  <div className="text-2xs text-fg-subtle">Median</div>
                  <div className="num text-sm text-fg">{formatKrPerSqm(216)}</div>
                </div>
                <div>
                  <div className="text-2xs text-fg-subtle">P25</div>
                  <div className="num text-sm text-fg">{formatKrPerSqm(192)}</div>
                </div>
                <div>
                  <div className="text-2xs text-fg-subtle">P75</div>
                  <div className="num text-sm text-fg">{formatKrPerSqm(241)}</div>
                </div>
              </div>
              <p className="mt-2 text-2xs text-fg-muted">Recomputing… 1 comparable excluded · {formatDate(new Date())}</p>
            </div>
          </div>
        </Section>

        <Section
          id="comparables"
          title="Comparables (M4)"
          description="Decision 9: right panel only on unit-detail comparables. Decision 10: exclude → progress bar + stale aggregates + inline undo. The pieces below render in isolation against fixed sample data so visual diffs catch unintended drift."
        >
          <div className="flex flex-col gap-4">
            <AggregateStats aggregate={sampleAggregate} target={sampleTarget} />
            <AggregateStats
              aggregate={sampleAggregate}
              target={sampleTarget}
              recomputing
              stale
            />
            <div className="rounded-sm border border-rule bg-surface p-3">
              <ScoreHistogram comparables={sampleComparables} aggregate={sampleAggregate} target={sampleTarget} />
            </div>
            <UndoStrip
              comparable={sampleComparables[0]}
              onUndo={() => undefined}
              onDismiss={() => undefined}
            />
            <ul className="overflow-hidden rounded-sm border border-rule bg-surface">
              <ComparableRow comparable={sampleComparables[0]} onExclude={() => undefined} expandedByDefault />
              <ComparableRow comparable={sampleComparables[1]} onExclude={() => undefined} />
              <ComparableRow comparable={sampleComparables[2]} excluded onExclude={() => undefined} />
            </ul>
          </div>
        </Section>
      </div>
    </div>
  );
}

const sampleTarget: ComparablesTarget = {
  kind: 'unit',
  unitId: 'unit-00042',
  address: 'Vesterbrogade 25, 2. tv',
  postnummer: '1620',
  city: 'København V',
  m2: 78.4,
  rooms: 3,
  yearBuilt: 1932,
  rentKrPerMonth: 18500,
  krPerSqm: 236
};

const sampleComparables: ComparableDTO[] = [
  {
    id: 'cmp-1',
    unitId: 'unit-00133',
    rank: 1,
    distanceM: 280,
    address: 'Sønder Boulevard 12, 4.',
    postnummer: '1620',
    city: 'København V',
    m2: 76.0,
    rooms: 3,
    yearBuilt: 1928,
    rentKrPerMonth: 17900,
    krPerSqm: 235,
    source: 'BoligPortal',
    listedAt: '2026-04-22T08:00:00Z',
    score: {
      total: 0.91,
      components: {
        sizeMatch: 0.96,
        roomsMatch: 1.0,
        yearMatch: 0.93,
        distance: 0.94,
        recency: 0.86,
        sourceQuality: 0.95
      }
    },
    constraintsPassed: ['±15% m²', '±1 rm', '±25 yr', 'same postnr']
  },
  {
    id: 'cmp-2',
    unitId: 'unit-00298',
    rank: 2,
    distanceM: 1340,
    address: 'Istedgade 84, 3. th',
    postnummer: '1650',
    city: 'København V',
    m2: 81.5,
    rooms: 3,
    yearBuilt: 1944,
    rentKrPerMonth: 18900,
    krPerSqm: 232,
    source: 'Lejebolig',
    listedAt: '2026-04-09T08:00:00Z',
    score: {
      total: 0.78,
      components: {
        sizeMatch: 0.88,
        roomsMatch: 1.0,
        yearMatch: 0.79,
        distance: 0.73,
        recency: 0.7,
        sourceQuality: 0.88
      }
    },
    constraintsPassed: ['±15% m²', '±1 rm', '±25 yr']
  },
  {
    id: 'cmp-3',
    unitId: 'unit-00420',
    rank: 0,
    distanceM: 1860,
    address: 'Frederiksberg Allé 22, 1.',
    postnummer: '1820',
    city: 'Frederiksberg',
    m2: 92.0,
    rooms: 4,
    yearBuilt: 1908,
    rentKrPerMonth: 21400,
    krPerSqm: 232,
    source: 'BoligZonen',
    listedAt: '2026-03-14T08:00:00Z',
    score: {
      total: 0.61,
      components: {
        sizeMatch: 0.55,
        roomsMatch: 0.7,
        yearMatch: 0.6,
        distance: 0.62,
        recency: 0.5,
        sourceQuality: 0.82
      }
    },
    constraintsPassed: ['±25% m²']
  }
];

const sampleAggregate: ComparablesAggregate = {
  count: 2,
  median: 233,
  p25: 230,
  p75: 235,
  min: 218,
  max: 248
};

